import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import {
  fetchERequisitionList,
  deleteDraftERequisition,
  fetchERequisitionMaster,
  syncERequisition,
  ERequisitionRequest
} from '../../../core/data/redux/eRequisitionSlice';
import {
  getRequestStatusConfig,
  getRequestStatusLabel,
} from '../../../core/data/constants/requestStatus';
import {
  apiMessage,
  formTypeLabel,
  formatDisplayDate,
  isApiSuccess,
  isDraftRecord,
  lookupReference,
  statusCode,
  unwrapList,
  unwrapMaster,
} from './eRequisition.shared';

export const getRequestTypeLabel = (code?: string, masterList?: any[]) => formTypeLabel(code, masterList);

export const getItemTypeLabel = (code?: string, masterList?: any[]) => {
  if (!code) return '-';
  const cleanCode = String(code).trim();
  if (Array.isArray(masterList)) {
    const found = masterList.find((m: any) => String(m.Value ?? m.code ?? '').trim() === cleanCode);
    if (found?.Ddtext || found?.label) return String(found.Ddtext || found.label).trim();
  }
  return code;
};

export const getStatusBadgeClass = (statusStr?: string) => {
  if (String(statusStr || '').toUpperCase() === 'DRAFT') {
    return 'badge bg-secondary-subtle text-secondary border border-secondary-subtle';
  }
  return getRequestStatusConfig(statusStr).badgeClass;
};

const ERequisitionITList: React.FC = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const eState = useSelector((state: RootState) => state.eRequisitionState);

  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', requestStatus: '', query: '' });
  const [page, setPage] = useState(1);
  const [deleteItem, setDeleteItem] = useState<ERequisitionRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadList = () => {
    const status = filters.requestStatus || '';
    const payload: any = {
      page,
      limit: 10,
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      requestStatus: status,
    };
    if (status) payload.status = status.toLowerCase();
    dispatch(fetchERequisitionList(payload));
  };

  useEffect(() => {
    dispatch(fetchERequisitionMaster({}));
    loadList();
  }, [dispatch, page]);

  const master = unwrapMaster(eState?.masterData);
  const formMasterList = master?.Form_Type || [];
  const allItemsList: ERequisitionRequest[] = unwrapList(eState?.listData);

  const itemsList = allItemsList.filter(item => {
    if (filters.query) {
      const q = filters.query.toLowerCase().trim();
      const ref = String(item.ReferenceNumber || item.referenceNo || '').toLowerCase();
      const emp = String(item.EmpID || '').toLowerCase();
      if (!ref.includes(q) && !emp.includes(q)) return false;
    }
    return true;
  });

  const handleClearFilter = () => {
    const reset = { startDate: '', endDate: '', requestStatus: '', query: '' };
    setFilters(reset);
    setPage(1);
    dispatch(fetchERequisitionList({ page: 1, limit: 10, startDate: '', endDate: '', requestStatus: '' }));
  };

  const handleDeleteDraft = async () => {
    if (!deleteItem) return;
    if (deleteItem.id == null || deleteItem.id === '') {
      toast.error('Draft id is missing. Sync the list and try again.');
      return;
    }
    setIsDeleting(true);
    try {
      const res = await dispatch(deleteDraftERequisition({ id: deleteItem.id }));
      if (deleteDraftERequisition.fulfilled.match(res) && isApiSuccess(res.payload)) {
        toast.success(apiMessage(res.payload, 'Draft deleted successfully'));
        setDeleteItem(null);
        loadList();
      } else {
        toast.error(apiMessage(res.payload, 'Failed to delete draft'));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error deleting draft');
    } finally {
      setIsDeleting(false);
    }
  };

  const stats = [
    { label: 'Total Requests', val: allItemsList.length, color: 'primary', icon: 'ti-files' },
    { label: 'In-Process / Pending', val: allItemsList.filter(i => ['N', 'T', 'P', 'B'].includes(statusCode(i))).length, color: 'warning', icon: 'ti-clock' },
    { label: 'Approved / Completed', val: allItemsList.filter(i => ['A', 'D', 'S'].includes(statusCode(i))).length, color: 'success', icon: 'ti-circle-check' },
    { label: 'Drafts', val: allItemsList.filter(i => isDraftRecord(i)).length, color: 'secondary', icon: 'ti-file-pencil' }
  ];

  const handleSync = async () => {
    try {
      const res = await dispatch(syncERequisition({ type: 'All' }));
      if (syncERequisition.fulfilled.match(res) && isApiSuccess(res.payload)) {
        toast.success(apiMessage(res.payload, 'Requisitions synced successfully'));
        loadList();
      } else {
        toast.error(apiMessage(res.payload, 'Failed to sync'));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error syncing requisitions');
    }
  };

  const listLoading = eState.action === 'list' && eState.loading === 'pending';

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1 text-dark fw-bold"><i className="ti ti-devices me-2 text-danger"></i>E-Requisition (IT)</h3>
            <nav><ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><Link to={routes.employeeDashboard || '#'}>Dashboard</Link></li>
              <li className="breadcrumb-item active">E-Requisition (IT)</li>
            </ol></nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowFilter(!showFilter)}>
              <i className={`ti ti-filter-${showFilter ? 'off' : 'check'} me-1`}></i>{showFilter ? 'Hide Filters' : 'Filters'}
            </button>
            <button className="btn btn-outline-primary btn-sm" onClick={loadList} disabled={listLoading}>
              <i className={`ti ti-refresh ${listLoading ? 'spin' : ''} me-1`}></i>Refresh
            </button>
            <button className="btn btn-outline-info btn-sm" onClick={handleSync} disabled={eState.action === 'sync' && eState.loading === 'pending'}>
              <i className={`ti ti-reload ${(eState.action === 'sync' && eState.loading === 'pending') ? 'spin' : ''} me-1`}></i>Sync
            </button>
            <Link to={routes.eRequisitionIT.create} className="btn btn-danger btn-sm">
              <i className="ti ti-plus me-1"></i>Create IT Request
            </Link>
          </div>
        </div>

        <div className="row g-3 mb-4">
          {stats.map((s, idx) => (
            <div className="col-md-3" key={idx}>
              <div className="card border-0 shadow-sm rounded-3">
                <div className="card-body p-3 d-flex align-items-center justify-content-between">
                  <div><span className="text-muted small fw-medium d-block">{s.label}</span><h4 className={`fw-bold text-${s.color} mb-0`}>{s.val}</h4></div>
                  <div className={`avatar avatar-md bg-${s.color}-subtle text-${s.color} rounded-circle d-flex align-items-center justify-content-center`}><i className={`ti ${s.icon} fs-20`}></i></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showFilter && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <form onSubmit={(e) => { e.preventDefault(); setPage(1); loadList(); }}>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold small">Search Query</label>
                    <input type="text" className="form-control form-control-sm" placeholder="Ref No or Emp ID..." value={filters.query} onChange={e => setFilters({ ...filters, query: e.target.value })} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold small">Request Status</label>
                    <select className="form-select form-select-sm" value={filters.requestStatus} onChange={e => setFilters({ ...filters, requestStatus: e.target.value })}>
                      <option value="">All</option>
                      <option value="N">New</option>
                      <option value="T">To Be Approved</option>
                      <option value="P">Partially Approved</option>
                      <option value="B">To Be Processed</option>
                      <option value="A">Approved</option>
                      <option value="R">Rejected</option>
                      <option value="D">Processing Complete</option>
                    </select>
                  </div>
                  <div className="col-md-3"><label className="form-label fw-semibold small">Start Date</label><input type="date" className="form-control form-control-sm" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} /></div>
                  <div className="col-md-3"><label className="form-label fw-semibold small">End Date</label><input type="date" className="form-control form-control-sm" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} /></div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClearFilter}>Reset</button>
                    <button type="submit" className="btn btn-danger btn-sm">Apply</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark"><i className="ti ti-list me-2 text-danger"></i>IT Equipment & Portal Requests</h5>
            <span className="badge bg-light text-dark border">{itemsList.length} Records</span>
          </div>
          <div className="card-body p-0">
            {listLoading ? (
              <div className="text-center py-5"><div className="spinner-border text-danger" role="status"></div></div>
            ) : itemsList.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" style={{ tableLayout: 'fixed', width: '100%', minWidth: 980 }}>
                  <colgroup>
                    <col style={{ width: 168 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 280 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 168 }} />
                  </colgroup>
                  <thead className="table-light text-secondary">
                    <tr>
                      <th className="text-nowrap">Ref No</th>
                      <th className="text-nowrap">Status</th>
                      <th>Request Form Type</th>
                      <th className="text-nowrap">Creation Date</th>
                      <th>Approver</th>
                      <th>Remarks</th>
                      <th className="text-end text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsList.map((item, idx) => {
                      const refNo = lookupReference(item);
                      const isRealDraft = isDraftRecord(item);
                      const code = statusCode(item);
                      const formType = formTypeLabel(item.SelectRequestFromType, formMasterList, item.SelectRequestFromTypeText);
                      const createdDate = formatDisplayDate(item.RequestCreationDate || item.requestCreationDate || item.createdAt || item.created_at) || '-';
                      const approverId = String(item.ApproverUserID || '').trim();
                      const approverName = String(item.ApproverName || '').trim();
                      const approver = approverName && approverId ? `${approverName} (${approverId})` : (approverName || approverId || '-');
                      const rawRemark = item.HRemarks || item.Remarks || item.remarks;
                      const rem = typeof rawRemark === 'string' ? rawRemark.trim() : '';
                      const rowKey = String(item.id || refNo || idx);
                      const statusLabel = code === 'DRAFT' ? 'Draft' : getRequestStatusLabel(code);

                      return (
                        <tr key={rowKey}>
                          <td>
                            <span className="d-block fw-semibold text-dark text-truncate" title={refNo || '-'} style={{ maxWidth: '100%' }}>
                              {refNo || '-'}
                            </span>
                          </td>
                          <td>
                            <span className={`${getStatusBadgeClass(code)} text-wrap`} style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>
                              {statusLabel}
                            </span>
                          </td>
                          <td>
                            <span className="d-block text-dark" style={{ whiteSpace: 'normal', lineHeight: 1.35 }} title={formType}>
                              {formType}
                            </span>
                          </td>
                          <td className="text-muted text-nowrap">{createdDate}</td>
                          <td>
                            <span className="d-block text-truncate" title={approver} style={{ maxWidth: '100%' }}>
                              {approver}
                            </span>
                          </td>
                          <td>
                            <span className="d-block text-muted text-truncate" title={rem || '-'} style={{ maxWidth: '100%' }}>
                              {rem || '-'}
                            </span>
                          </td>
                          <td className="text-end text-nowrap">
                            {isRealDraft ? (
                              <div className="d-inline-flex align-items-center gap-1">
                                <Link
                                  to={routes.eRequisitionIT.edit.replace(':id', String(refNo || item.id))}
                                  state={{ draftRequest: item }}
                                  className="btn btn-sm btn-outline-warning py-1 px-2"
                                  title="Continue draft"
                                >
                                  Continue
                                </Link>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger py-1 px-2"
                                  onClick={() => setDeleteItem(item)}
                                  title="Delete Draft"
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <Link to={routes.eRequisitionIT.details.replace(':id', String(refNo))} className="btn btn-sm btn-outline-danger py-1 px-2">Details</Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5"><p className="text-muted">No Requisitions Found</p></div>
            )}
          </div>
        </div>

        {deleteItem && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content border-0 shadow">
                <div className="modal-body text-center p-4">
                  <i className="ti ti-alert-triangle text-danger fs-32 mb-2 d-block"></i>
                  <h6>Delete Draft #{deleteItem.ReferenceNumber || deleteItem.id}?</h6>
                  <p className="text-muted small">This action cannot be undone.</p>
                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setDeleteItem(null)} disabled={isDeleting}>Cancel</button>
                    <button className="btn btn-sm btn-danger" onClick={handleDeleteDraft} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERequisitionITList;
